import { buildPledgePayTableContext } from './pledge-pay-surface';
import { setDafCardType } from '../components/credit-card-entry.js';
import {
    dedupePledgesByKey,
    getSelectedPayPledges,
    setSelectedPayPledges,
} from './pay-pledge-map';
import { resetPledgePayOpenCredit } from './pledge-pay-open-credit-context';
import {
    mgrPledgeBillInitExpiryInputMask,
    mgrPledgeModalSyncPaymentMethodPanels,
    resetMgrPledgeBillDafCardType,
} from './pledge-pay-method';
import { resetSurchargeRows } from './pledge-pay-surcharge';
import { releasePledgeBillSubmitLock } from './pledge-pay-bill-submit';
import { initPledgeScheduleStartDatepicker } from '../shared/pledge-schedule-start-date.js';
import {
    mgrPledgeModalSyncOpenPaymentVisibility,
    mgrPledgeModalSyncPaymentTableFooter,
    pledgePayBillUpdateSummary,
    pledgePaymentBodySyncOpenPaymentSummary,
    restorePledgePayPaymentMethodForRecurring,
    setPledgePayNowButtonLabel,
} from './pledge-pay-balance';
import { updatePledgePayNowButtonEnabledState } from './pledge-pay-amount';
import { isMgrPledgePayShell, pledgePayBodyFromShell, pledgePayUiRoot } from './pledge-pay-utils';
import { renderPledgePaymentTable } from './pledge-pay-table';
import { applyPendingRecurringMode } from './pledge-pay-pending-recurring';
import {
    calculateRecurringPaymentAmount,
    destroyRecurringTable,
    initializeRecurringTable,
    setPledgeStartDateByFrequency,
    setRecurringNoteVisibility,
} from './pledge-pay-recurring';
function buildGatewayCampaignGroups(pledges) {
    const groups = new Map();

    for (const { gateway_id: gatewayId, campaign } of pledges || []) {
        if (!gatewayId) continue;

        if (!groups.has(gatewayId)) {
            groups.set(gatewayId, new Set());
        }

        groups.get(gatewayId).add(campaign || 'Unknown campaign');
    }

    return [...groups].map(([gatewayId, campaigns]) => ({
        gatewayId,
        campaigns: [...campaigns],
    })).sort((a, b) =>
        String(a.gatewayId).localeCompare(String(b.gatewayId), undefined, { numeric: true })
    );
}

function updateMultiGatewayPaymentModeWarning($container, pledges, payType = 'pay') {
    if (!$container || !$container.length) {
        return;
    }

    if (payType === 'mark') {
        $container.find('#multi_gateway_payment_mode_warning').hide();
        return;
    }

    const gatewayIds = (pledges || [])
        .map((pledge) => pledge.gateway_id)
        .filter((gateway_id) => gateway_id !== null && gateway_id !== undefined && gateway_id !== '');
    const hasMultipleGateways = new Set(gatewayIds).size > 1;

    const $recurringPayment = $container.find('#recurring_payment');
    const $fullPayment = $container.find('#full_payment');
    const $multiGatewayWarning = $container.find('#multi_gateway_payment_mode_warning');
    const $multiGatewayWarningDetails = $container.find('#multi_gateway_payment_mode_warning_details');
    const $recurringPaymentLabel = $container.find('label[for="recurring_payment"]');

    const shouldDisableRecurring = payType === 'pay'
        && (pledges || []).length > 1
        && hasMultipleGateways;

    if (shouldDisableRecurring) {
        if ($recurringPayment.length) {
            $recurringPayment.prop('checked', false).prop('disabled', true);
        }
        if ($recurringPaymentLabel.length) {
            $recurringPaymentLabel.addClass('is-disabled');
        }
        if ($fullPayment.length) {
            $fullPayment.prop('checked', true);
        }
        if ($multiGatewayWarning.length) {
            $multiGatewayWarning.show();
        }
        if ($multiGatewayWarningDetails.length) {
            $multiGatewayWarningDetails.empty();
            const groupedCampaigns = buildGatewayCampaignGroups(pledges);
            if (groupedCampaigns.length) {
                const $intro = $('<div>').text(
                    "If you'd like to set up recurring payments, you can select the pledges that belong to these campaigns separately:"
                );
                const $list = $('<ul class="mb-0 pl-3">');
                groupedCampaigns.forEach((group) => {
                    const campaignList = group.campaigns.length ? group.campaigns.join(', ') : 'No campaigns selected';
                    $list.append($('<li>').text(`${campaignList}`));
                });
                $multiGatewayWarningDetails.append($intro, $list);
            }
        }

        if ($container.attr('id') === 'pledge_pay_body' && $fullPayment.length) {
            handlePledgePaymentModeChange($fullPayment);
        }
        return;
    }

    if ($multiGatewayWarning.length) {
        $multiGatewayWarning.hide();
    }
    if ($multiGatewayWarningDetails.length) {
        $multiGatewayWarningDetails.empty();
    }
    if ($recurringPayment.length) {
        $recurringPayment.prop('disabled', false);
    }
    if ($recurringPaymentLabel.length) {
        $recurringPaymentLabel.removeClass('is-disabled');
    }
}

function handlePledgePaymentModeChange($input) {
    const $parentDiv = pledgePayBodyFromShell(pledgePayUiRoot($input));
    if (!$parentDiv.length) {
        return;
    }

    const isRecurring = $input.val() === 'recurring';
    const $recurringSetup = $parentDiv.find('.recurring-setup');
    const $payNowButton = $parentDiv.find('#pay_now_button');
    const $home = $parentDiv.find('#mgr_recurring_fields_home');
    if ($home.length) {
        $home.show();
    }

    if (isRecurring) {
        $recurringSetup.removeClass('pledge-pay-hidden').show()
            .find('input:not([type="checkbox"]), select, textarea')
            .prop('required', true);
        if ($payNowButton.length) {
            setPledgePayNowButtonLabel($payNowButton, 'Setup Recurring Payment');
            $payNowButton.attr('title', 'Setup recurring payment schedule');
        }

        setTimeout(function () {
            initPledgeScheduleStartDatepicker($parentDiv);
        }, 100);

        setTimeout(function () {
            calculateRecurringPaymentAmount($parentDiv);
        }, 100);
    } else {
        setRecurringNoteVisibility($parentDiv.find('#recurring-payment-display'), false);
        setRecurringNoteVisibility($parentDiv.find('#last-payment-note'), false);
        $recurringSetup.hide().addClass('pledge-pay-hidden')
            .find('input, select, textarea')
            .prop('required', false);
        if ($payNowButton.length && !$parentDiv.closest('#setPledgePaymentModal').length) {
            setPledgePayNowButtonLabel($payNowButton, 'Make Payment');
            $payNowButton.attr('title', 'You must save Matanah amounts prior to paying');
        }
    }

    if (isRecurring) {
        const $frequencySelect = $parentDiv.find('select[name="frequency"]');
        if ($frequencySelect.length) {
            setPledgeStartDateByFrequency($parentDiv, $frequencySelect.val());
        }
    }

    const selectedPledges = getSelectedPayPledges();
    if (selectedPledges.length) {
        const $modBodyInfo = $parentDiv.find('#set_pledge_payment_table');
        if ($modBodyInfo.length) {
            const tableContext = buildPledgePayTableContext(
                $parentDiv,
                $parentDiv.find('#payment_type').val() || 'pay',
                isRecurring
            );
            const result = renderPledgePaymentTable(
                selectedPledges,
                isRecurring,
                $parentDiv,
                tableContext
            );

            $parentDiv.find('#total_amount').val(result.totalAmount);

            updatePledgePayNowButtonEnabledState($parentDiv);
        }
    }

    if (isRecurring) {
        initializeRecurringTable($parentDiv);
    } else {
        destroyRecurringTable($parentDiv);
    }

    pledgePaymentBodySyncOpenPaymentSummary($parentDiv, isRecurring);
}

function showPledgePayTable($container, pledges, payType, tableContext) {
    const uniquePledges = dedupePledgesByKey(pledges);
    if (!uniquePledges?.length) {
        return null;
    }
    setSelectedPayPledges(uniquePledges);
    const isPendingRecurring = applyPendingRecurringMode($container, uniquePledges);
    updateMultiGatewayPaymentModeWarning($container, uniquePledges, payType);
    const resolvedContext = tableContext
        || buildPledgePayTableContext($container, payType, isPendingRecurring);
    const result = renderPledgePaymentTable(uniquePledges, isPendingRecurring, $container, resolvedContext);
    if (!result) {
        return null;
    }
    $container.find('#total_amount').val(result.totalAmount);
    updatePledgePayNowButtonEnabledState($container);
    const modeIsRecurring = isPendingRecurring
        || $container.find('input[name="payment_mode"]:checked').val() === 'recurring';
    if (modeIsRecurring) {
        initializeRecurringTable($container);
        calculateRecurringPaymentAmount($container);
    }
    pledgePaymentBodySyncOpenPaymentSummary(
        $container.is('#pledge_pay_body') ? $container : $container.find('#pledge_pay_body').first(),
        modeIsRecurring
    );
    return result;
}

function resetOpenPaymentFields($modal) {
    if ($modal.find('#mgr_use_open_payment_balance').length) {
        $modal.find('#mgr_use_open_payment_balance').prop('checked', false);
    }
    if ($modal.find('#mgr_open_payment_apply_amount').length) {
        $modal.find('#mgr_open_payment_apply_amount').val('');
    }
    $modal.find('.mgr-gateway-use-credit').prop('checked', false);
    $modal.find('.mgr-gateway-credit-apply').val('').prop('disabled', true);
    $modal.find('.mgr-gateway-credit-panel').addClass('mgr-gateway-credit-inactive');
}

$(document).on('change', 'input[name="payment_mode"]', function () {
    if (!$(this).prop('checked')) {
        return;
    }
    const $input = $(this);
    handlePledgePaymentModeChange($input);
    const $shell = pledgePayUiRoot($input);
    if (!isMgrPledgePayShell($shell)) {
        return;
    }
    mgrPledgeModalSyncOpenPaymentVisibility($shell);
    mgrPledgeModalSyncPaymentTableFooter($shell);
    mgrPledgeModalSyncPaymentMethodPanels($shell);
});

$(document).on('hidden.bs.modal', '#setPledgePaymentModal', function () {
    resetSurchargeRows($(this));
    resetPledgePayOpenCredit();
    const $m = $(this);
    const $body = $m.find('#pledge_pay_body');
    if ($body.length) {
        pledgePaymentBodySyncOpenPaymentSummary($body, false);
        restorePledgePayPaymentMethodForRecurring($body);
    }
    $m.find('#mgr_pledge_bill_name,#mgr_pledge_bill_card,#mgr_pledge_bill_expiry_month,#mgr_pledge_bill_expiry_year,#mgr_pledge_bill_cvv2,#mgr_pledge_bill_avs_zip').val('');
    $m.find('#mgr_pledge_bill_save_card,#mgr_pledge_bill_use_otp').prop('checked', false);
    $m.find('#mgr_pledge_bill_cc_ajax_err').hide();
    resetMgrPledgeBillDafCardType();
    releasePledgeBillSubmitLock($m);
});

$(document).on('shown.bs.modal', '#setPledgePaymentModal', function () {
    const $m = $(this);
    if ($m.find('#mgr_pledge_bill_card_fields_wrap').length) {
        setDafCardType('mgr_pledge_bill_', 'credit');
    }
    mgrPledgeBillInitExpiryInputMask($m);
    pledgePayBillUpdateSummary($m);
});

export {
    handlePledgePaymentModeChange,
    showPledgePayTable,
    resetOpenPaymentFields,
};
